"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreditUsageAttributionTag = exports.CreditUsageAttribution = exports.CreditReportingCategory = exports.CreditReport = exports.CreditPeriodType = exports.CreditTrace = exports.CreditUsageMap = exports.CreditLedgerEntryContextType = exports.CreditLedgerOperationType = exports.UNKNOWN_RESOLVED_PRICE_BUCKET = exports.UNKNOWN_PRICE_BUCKET = exports.StripeSyncStatus = exports.CreditUsageVersion = exports.CreditGlobalSettings = exports.CreditTokenDivisors = exports.CreditPeriod = exports.StaticCreditPeriod = exports.TimeBasedCreditPeriod = exports.CreditPeriodOffsetForStripeMs = exports.CreditPeriodSummaryKey = exports.SpendingLimit = exports.TotalCreditAmount = exports.CreditAmount = exports.TokenAmount = exports.TokenToCreditDivisor = exports.CreditTokenType = exports.TokenUsageApplicationType = exports.CreditBalanceType = void 0;
exports.creditPeriodFromSummaryKey = creditPeriodFromSummaryKey;
exports.accountUidFromSummaryKey = accountUidFromSummaryKey;
exports.creditPeriodSummaryKey = creditPeriodSummaryKey;
exports.isStaticCreditPeriodSummary = isStaticCreditPeriodSummary;
exports.isTimeBasedCreditPeriodSummary = isTimeBasedCreditPeriodSummary;
exports.isCreditLedgerEntryV3 = isCreditLedgerEntryV3;
exports.upgradeLedgerEntryV2ToV3 = upgradeLedgerEntryV2ToV3;
exports.isCreditLedgerEntryV4 = isCreditLedgerEntryV4;
exports.upgradeLedgerEntryV3ToV4 = upgradeLedgerEntryV3ToV4;
exports.isCreditLedgerEntryV5 = isCreditLedgerEntryV5;
exports.upgradeLedgerEntryV4ToV5 = upgradeLedgerEntryV4ToV5;
exports.isCreditLedgerEntryV6 = isCreditLedgerEntryV6;
exports.upgradeLedgerEntryV5ToV6 = upgradeLedgerEntryV5ToV6;
exports.isCreditLedgerEntryV7 = isCreditLedgerEntryV7;
exports.upgradeLedgerEntryV6ToV7 = upgradeLedgerEntryV6ToV7;
exports.isCreditLedgerEntryV2 = isCreditLedgerEntryV2;
exports.upgradeLedgerEntryToV2 = upgradeLedgerEntryToV2;
exports.normalizeLedgerEntry = normalizeLedgerEntry;
const zod_1 = require("zod");
const structure_1 = require("../payment/structure");
/**
 * The type of balance a credit transaction is applied to
 *
 * For a time-based credit period:
 *  - "free" credits are taken from a preallocated budget set by the account's plan category, representing credits that
 *    the account does not have to pay more money for using.
 *  - "paid" credits are pay-as-you-go credits that we will charge to the account's Stripe account at the end of the month
 *
 * For a static credit period:
 *  - "free" credits are taken from a preallocated block of credits that the account has already paid for, e.g. by purchasing
 *    an enterprise subscription
 *  - "paid" credits are overage credits, representing usage beyond the preallocated block - the account may be asked to
 *    pay for these in-person (e.g. by purchasing another block of credits), but won't be automatically charged for them
 */
exports.CreditBalanceType = zod_1.z.enum(["copilot-free", "copilot-paid"]);
/**
 * Application types to support feature-specific token divisors
 *
 * autopilot:  all llm usage by the main autopilot graph (plan, execute, action application), but not its tools
 * autoPlacement: auto-placement feature for component placement
 * autoLayout: auto-layout feature for PCB routing
 * actionsFromChat: actionGeneration from chat (eg. add a component)
 * general: chat tools, classifyNets, also the default fallback / catch-all
 * chat: main chat agent and plan generation (exlcuding tools)
 * @deprecated specializedAgent: kept for backwards compatibility with existing ledger entries, do not use for new writes
 *
 */
exports.TokenUsageApplicationType = zod_1.z.enum([
    "autopilot",
    "autoPlacement",
    "autoLayout",
    "actionsFromChat",
    "chat",
    "general",
    "specializedAgent",
]);
exports.CreditTokenType = zod_1.z.enum([
    // OpenAI
    "openAiUsage-gpt-4o-mini-2024-07-18-prompt",
    "openAiUsage-gpt-4o-mini-2024-07-18-completion",
    "openAiUsage-gpt-4o-mini-prompt",
    "openAiUsage-gpt-4o-mini-completion",
    "openAiUsage-gpt-4o-2024-05-13-prompt",
    "openAiUsage-gpt-4o-2024-05-13-completion",
    "openAiUsage-gpt-4o-2024-08-06-prompt",
    "openAiUsage-gpt-4o-2024-08-06-completion",
    "openAiUsage-gpt-4o-prompt",
    "openAiUsage-gpt-4o-completion",
    "openAiUsage-gpt-4-1106-preview-prompt",
    "openAiUsage-gpt-4-1106-preview-completion",
    "openAiUsage-gpt-4-turbo-prompt",
    "openAiUsage-gpt-4-turbo-completion",
    "openAiUsage-gpt-4-vision-prompt",
    "openAiUsage-gpt-4-vision-completion",
    "openAiUsage-gpt-4-8k-prompt",
    "openAiUsage-gpt-4-8k-completion",
    "openAiUsage-gpt-4-32k-prompt",
    "openAiUsage-gpt-4-32k-completion",
    "openAiUsage-gpt-4-prompt",
    "openAiUsage-gpt-4-completion",
    "openAiUsage-gpt-4.5-preview-2025-02-27-prompt",
    "openAiUsage-gpt-4.5-preview-2025-02-27-completion",
    "openAiUsage-gpt-4.5-preview-prompt",
    "openAiUsage-gpt-4.5-preview-completion",
    "openAiUsage-o3-mini-2025-01-31-prompt",
    "openAiUsage-o3-mini-2025-01-31-completion",
    "openAiUsage-o3-mini-prompt",
    "openAiUsage-o3-mini-completion",
    "openAiUsage-o3-2025-04-16-prompt",
    "openAiUsage-o3-2025-04-16-completion",
    "openAiUsage-o3-prompt",
    "openAiUsage-o3-completion",
    "openAiUsage-o4-mini-2025-04-16-prompt",
    "openAiUsage-o4-mini-2025-04-16-completion",
    "openAiUsage-o4-mini-prompt",
    "openAiUsage-o4-mini-completion",
    "openAiUsage-gpt-4.1-2025-04-14-prompt",
    "openAiUsage-gpt-4.1-2025-04-14-completion",
    "openAiUsage-gpt-4.1-prompt",
    "openAiUsage-gpt-4.1-completion",
    "openAiUsage-gpt-5-2025-08-07-prompt",
    "openAiUsage-gpt-5-2025-08-07-completion",
    "openAiUsage-gpt-5.1-2025-11-13-prompt",
    "openAiUsage-gpt-5.1-2025-11-13-completion",
    "openAiUsage-gpt-5.2-2025-12-11-prompt",
    "openAiUsage-gpt-5.2-2025-12-11-completion",
    "openAiUsage-gpt-5.2-pro-2025-12-11-prompt",
    "openAiUsage-gpt-5.2-pro-2025-12-11-completion",
    "openAiUsage-gpt-5.1-codex-max-prompt",
    "openAiUsage-gpt-5.1-codex-max-completion",
    "openAiUsage-gpt-5-nano-2025-08-07-prompt",
    "openAiUsage-gpt-5-nano-2025-08-07-completion",
    "openAiUsage-gpt-5-mini-2025-08-07-prompt",
    "openAiUsage-gpt-5-mini-2025-08-07-completion",
    "openAiUsage-o1-2024-12-17-prompt",
    "openAiUsage-o1-2024-12-17-completion",
    "openAiUsage-o1-preview-prompt",
    "openAiUsage-o1-preview-completion",
    "openAiUsage-o1-mini-prompt",
    "openAiUsage-o1-mini-completion",
    "openAiUsage-o1-prompt",
    "openAiUsage-o1-completion",
    "openAiUsage-ft:gpt-4o-2024-08-06-prompt",
    "openAiUsage-ft:gpt-4o-2024-08-06-completion",
    "openAiUsage-ft:gpt-4o-mini-2024-07-18-prompt",
    "openAiUsage-ft:gpt-4o-mini-2024-07-18-completion",
    "openAiUsage-ft:gpt-3.5-turbo-prompt",
    "openAiUsage-ft:gpt-3.5-turbo-completion",
    // OpenAI Cached Prompt Tokens (for models that support prompt caching)
    "openAiUsage-gpt-4o-mini-2024-07-18-prompt-cached",
    "openAiUsage-gpt-4o-mini-prompt-cached",
    "openAiUsage-gpt-4o-2024-05-13-prompt-cached",
    "openAiUsage-gpt-4o-2024-08-06-prompt-cached",
    "openAiUsage-gpt-4o-prompt-cached",
    "openAiUsage-gpt-4.5-preview-2025-02-27-prompt-cached",
    "openAiUsage-gpt-4.5-preview-prompt-cached",
    "openAiUsage-o3-mini-2025-01-31-prompt-cached",
    "openAiUsage-o3-mini-prompt-cached",
    "openAiUsage-o3-2025-04-16-prompt-cached",
    "openAiUsage-o3-prompt-cached",
    "openAiUsage-o4-mini-2025-04-16-prompt-cached",
    "openAiUsage-o4-mini-prompt-cached",
    "openAiUsage-gpt-4.1-2025-04-14-prompt-cached",
    "openAiUsage-gpt-4.1-prompt-cached",
    "openAiUsage-gpt-5-2025-08-07-prompt-cached",
    "openAiUsage-gpt-5.1-2025-11-13-prompt-cached",
    "openAiUsage-gpt-5.2-2025-12-11-prompt-cached",
    "openAiUsage-gpt-5.2-pro-2025-12-11-prompt-cached",
    "openAiUsage-gpt-5.1-codex-max-prompt-cached",
    "openAiUsage-gpt-5-nano-2025-08-07-prompt-cached",
    "openAiUsage-gpt-5-mini-2025-08-07-prompt-cached",
    "openAiUsage-o1-2024-12-17-prompt-cached",
    "openAiUsage-o1-preview-prompt-cached",
    "openAiUsage-o1-mini-prompt-cached",
    "openAiUsage-o1-prompt-cached",
    "openAiUsage-ft:gpt-4o-2024-08-06-prompt-cached",
    "openAiUsage-ft:gpt-4o-mini-2024-07-18-prompt-cached",
    // Anthropic
    "anthropicUsage-claude-3-5-sonnet-20240620-prompt",
    "anthropicUsage-claude-3-5-sonnet-20240620-completion",
    "anthropicUsage-claude-3-opus-20240229-prompt",
    "anthropicUsage-claude-3-opus-20240229-completion",
    "anthropicUsage-claude-3-sonnet-20240229-prompt",
    "anthropicUsage-claude-3-sonnet-20240229-completion",
    "anthropicUsage-claude-3-haiku-20240307-prompt",
    "anthropicUsage-claude-3-haiku-20240307-completion",
    "anthropicUsage-claude-3-opus-prompt",
    "anthropicUsage-claude-3-opus-completion",
    // DeepPcb
    "deep-pcb-auto-layout",
    // Other
    "unknown-unknown-prompt",
    "unknown-unknown-completion",
]);
/**
 * The token to credit divisor is used to turn tokens used for a particular token type into a number of credits
 * consumed. (i.e. for a token-to-credit-divisor of 1024, 1024 tokens used would consume 1 credit). The number of
 * tokens is the dividend (number being divided by this number), and the number of credits is the quotient (result of
 * the division)
 *
 * We use a divisor because it's easier to deal with e.g. in LaunchDarkly - just be careful that it's never zero before
 * dividing by it!
 */
exports.TokenToCreditDivisor = zod_1.z.number().int().safe().nonnegative().gt(0).brand();
exports.TokenAmount = zod_1.z.number().safe().int().nonnegative().brand();
exports.CreditAmount = zod_1.z.number().safe().nonnegative().brand();
exports.TotalCreditAmount = zod_1.z.number().safe().int().nonnegative().brand();
exports.SpendingLimit = zod_1.z
    .number()
    .safe()
    .finite()
    .int()
    .transform((val) => Math.max(0, Math.min(999_999_00, val)))
    .brand();
exports.CreditPeriodSummaryKey = zod_1.z.string().refine((val) => {
    const matches = /^cc_(.+)_(.+)$/.exec(val);
    return matches !== null && !!exports.CreditPeriod.parse(matches[2]);
});
function creditPeriodFromSummaryKey(key) {
    return exports.CreditPeriod.parse(key.split("_")[2]);
}
function accountUidFromSummaryKey(key) {
    const uid = key.split("_")[1];
    if (!uid) {
        throw new Error(`Invalid account UID in credit period summary key: ${key}`);
    }
    return uid; // Can't parse further because UserUid is just a string
}
function creditPeriodSummaryKey(owner, period) {
    return `cc_${owner}_${period}`;
}
/**
 * The Stripe billing cycle is defined to start and end two hours after the Flux credit summarization period, to allow
 * time for final syncing
 */
exports.CreditPeriodOffsetForStripeMs = 2 * 60 * 60 * 1000;
/**
 * Most normal credit periods are time-based - they reset every month, and are named with a prefix of "p_" followed by
 * a date string that represents the month they're for
 */
exports.TimeBasedCreditPeriod = zod_1.z
    .string()
    .startsWith("p_")
    .length(8)
    .regex(/^p_2[0-9]{3}[01][0-9]$/)
    .refine((_val) => true);
/**
 * Sometimes, we preallocate credits for a specific purpose, such as enterprise sales, and when we do this we use a static
 * credit period. This is given a UUID
 *
 * To find the current "static" credit period, this identifier will be stored elsewhere, such as in a the credit account
 * settings
 */
exports.StaticCreditPeriod = zod_1.z
    .string()
    .startsWith("scp_")
    .length(40)
    .regex(/^scp_[0-9a-f-]{36}$/)
    .refine((_val) => true);
exports.CreditPeriod = zod_1.z.union([exports.TimeBasedCreditPeriod, exports.StaticCreditPeriod]);
/**
 * Token-to-credit divisor settings that control pricing for different models and applications
 */
exports.CreditTokenDivisors = zod_1.z.object({
    /**
     * Application-specific token-to-credit divisors that override the general tokenDivisors for specific applications
     *
     * This allows different applications (like "autopilot", "chat", etc.) to have different pricing for the same models.
     * If an application-specific divisor exists, it takes precedence over the general tokenDivisors.
     */
    applicationSpecificTokenDivisors: zod_1.z
        .record(exports.TokenUsageApplicationType, // application type (e.g., "autopilot", "chat")
    zod_1.z.record(zod_1.z.string(), exports.TokenToCreditDivisor))
        .optional(),
    /**
     * The token-to-credit divisors for each token type
     *
     * This can be freely changed by Flux at any time, because only new transactions will use the updated divisors. The
     * divisors are copied into each ledger entry, so we can see what they were when each transaction was recorded.
     *
     * This list is aspirationally supposed to be total, i.e. it should contain a divisor for every token type - but
     * in practicality, we want to be able to roll out new credit token types without needing to synchronize changes
     * with e.g. the LaunchDarkly settings in every environment at once. So, practically, if a credit token type is not
     * in this list, it is ignored (we do not use it when calculating credit usage)
     *
     * This field is optional for feature flag overrides (e.g., credit-pricing-experiment) where only
     * applicationSpecificTokenDivisors may be specified. For CreditGlobalSettings, this field is required.
     */
    tokenDivisors: zod_1.z.record(exports.CreditTokenType, exports.TokenToCreditDivisor).optional(),
});
/**
 * Global settings for the copilot credit system - we will generally try to configure these per-environment
 */
exports.CreditGlobalSettings = exports.CreditTokenDivisors.required({ tokenDivisors: true }).extend({
    /**
     * A Stripe price for each unit of paid credit
     *
     * This setting is logically controlled by global settings, plans and entitlements and then possibly overridden by
     * per-account settings
     *
     * When the next period is created, we check the usageVersion (see CreditAccountSettings), and iff that is v1 we
     * will use this price. So, changes to this value only take effect on the next monthly billing period, when we copy
     * it into the credit summary for that period. We also need to sync any changes to these values to the actual Stripe
     * subscriptions.
     *
     * It is NOT SAFE to access this value and assume it is the correct Stripe price to use for the credit subscription:
     * the correct price to use is that recorded on the current period summary.
     */
    paidCreditPrice: structure_1.StripePrice,
    /**
     * A Stripe price for each unit of paid credit, associated with a v2 meter, recording usage events with the newer Stripe API
     *
     * This setting is logically controlled by global settings, plans and entitlements and then possibly overridden by
     * per-account settings
     *
     * When the next period is created, we check the usageVersion (see CreditAccountSettings), and iff that is **v2** we
     * will use this price. So, changes to this value only take effect on the next monthly billing period, when we copy
     * it into the credit summary for that period. We also need to sync any changes to these values to the actual Stripe
     * subscriptions.
     */
    paidCreditPriceWithUsageMeter: structure_1.StripePrice,
    /**
     * Billing threshold settings that should be synced to the subscription when thresholds are in use
     */
    billingThresholds: zod_1.z.object({
        amountGte: zod_1.z.number(),
        /* TODO: usageGte?: number; - threshold in usage units (credits) */
        /* TODO: resetBillingCycleAnchor?: boolean; - allows billing cycle anchor reset (quasi start plan immediately) */
    }),
});
/**
 * Usage version constants for credit period summaries
 *
 * Defines which Stripe sync implementation to use for a given period.
 */
exports.CreditUsageVersion = zod_1.z.enum([
    /** v1 usage records API - sends pre-summarized gauge values (totals) to Stripe */
    "v1_gauge",
    /** v2 meter events API - sends individual ledger entries as delta events to Stripe */
    "v2_meter_events",
]);
/**
 * Zod schema for Stripe sync status values
 */
exports.StripeSyncStatus = zod_1.z.enum(["none", "pending", "sending", "sent", "error"]);
function isStaticCreditPeriodSummary(summary) {
    return exports.StaticCreditPeriod.safeParse(summary.period).success;
}
function isTimeBasedCreditPeriodSummary(summary) {
    return exports.TimeBasedCreditPeriod.safeParse(summary.period).success;
}
/**
 * Special price identifier for credits that were recorded before we started tracking prices per-entry.
 * When syncing to Stripe, this bucket should be resolved to the actual price from the existing subscription.
 */
exports.UNKNOWN_PRICE_BUCKET = "unknown";
/**
 * Special price identifier for credits that were originally in the "unknown" bucket but have been
 * resolved to an actual Stripe price. This is kept as a record of how many credits were migrated.
 */
exports.UNKNOWN_RESOLVED_PRICE_BUCKET = "unknown_resolved";
/**
 * Type guard to check if a ledger entry is V3
 *
 * @param entry - Ledger entry to check
 * @returns True if the entry is V3, false otherwise
 */
function isCreditLedgerEntryV3(entry) {
    return "version" in entry && entry.version === 3;
}
/**
 * Upgrade a V2 ledger entry to V3 structure
 *
 * @internal This function is used internally by normalizeLedgerEntry. Most code should
 * use normalizeLedgerEntry instead.
 *
 * V2 entries don't have price tracking, so paidCreditPriceId is set to undefined.
 *
 * @param entry - V2 ledger entry to upgrade
 * @returns Upgraded V3 ledger entry
 */
function upgradeLedgerEntryV2ToV3(entry) {
    return {
        ...entry,
        version: 3,
        paidCreditPriceId: undefined,
    };
}
/**
 * Type guard to check if a ledger entry is V4
 *
 * @param entry - Ledger entry to check
 * @returns True if the entry is V4, false otherwise
 */
function isCreditLedgerEntryV4(entry) {
    return "version" in entry && entry.version === 4;
}
/**
 * Upgrade a V3 ledger entry to V4 structure
 *
 * @internal This function is used internally by normalizeLedgerEntry. Most code should
 * use normalizeLedgerEntry instead.
 *
 * V3 entries don't have tags, so tags is set to undefined.
 *
 * @param entry - V3 ledger entry to upgrade
 * @returns Upgraded V4 ledger entry
 */
function upgradeLedgerEntryV3ToV4(entry) {
    return {
        ...entry,
        version: 4,
        tags: undefined,
    };
}
/**
 * Operation type for credit ledger entries
 *
 * Identifies the type of operation that triggered the credit usage.
 */
exports.CreditLedgerOperationType = zod_1.z.enum([
    "autoLayout",
    "autopilot",
    "orbit",
    "chat",
    "planning",
    "generation",
    "designReview",
    "datasheetImport",
    "sandbox",
    "generic",
]);
/**
 * Context type discriminator for credit ledger entries
 *
 * Used to distinguish between entries with operation context and entries without.
 */
exports.CreditLedgerEntryContextType = zod_1.z.enum(["operation", "none"]);
/**
 * Type guard to check if a ledger entry is V5
 *
 * @param entry - Ledger entry to check
 * @returns True if the entry is V5, false otherwise
 */
function isCreditLedgerEntryV5(entry) {
    return "version" in entry && entry.version === 5;
}
/**
 * Upgrade a V4 ledger entry to V5 structure
 *
 * @internal This function is used internally by normalizeLedgerEntry. Most code should
 * use normalizeLedgerEntry instead.
 *
 * V4 entries don't have context, so context is set to `{ type: "none" }`.
 *
 * @param entry - V4 ledger entry to upgrade
 * @returns Upgraded V5 ledger entry
 */
function upgradeLedgerEntryV4ToV5(entry) {
    return {
        ...entry,
        version: 5,
        context: { type: exports.CreditLedgerEntryContextType.enum.none },
    };
}
/**
 * Type guard to check if a ledger entry is V6
 *
 * @param entry - Ledger entry to check
 * @returns True if the entry is V6, false otherwise
 */
function isCreditLedgerEntryV6(entry) {
    return "version" in entry && entry.version === 6;
}
/**
 * Upgrade a V5 ledger entry to V6 structure
 *
 * @internal This function is used internally by normalizeLedgerEntry. Most code should
 * use normalizeLedgerEntry instead.
 *
 * V5 entries don't have pricingBreakdown, so it is set to undefined.
 *
 * @param entry - V5 ledger entry to upgrade
 * @returns Upgraded V6 ledger entry
 */
function upgradeLedgerEntryV5ToV6(entry) {
    return {
        ...entry,
        version: 6,
        pricingBreakdown: undefined,
    };
}
/**
 * Type guard to check if a ledger entry is V7
 *
 * @param entry - Ledger entry to check
 * @returns True if the entry is V7, false otherwise
 */
function isCreditLedgerEntryV7(entry) {
    return "version" in entry && entry.version === 7;
}
/**
 * Upgrade a V6 ledger entry to V7 structure
 *
 * @internal This function is used internally by normalizeLedgerEntry. Most code should
 * use normalizeLedgerEntry instead.
 *
 * V6 entries don't have threshold billing fields, so they are left as undefined.
 *
 * @param entry - V6 ledger entry to upgrade
 * @returns Upgraded V7 ledger entry
 */
function upgradeLedgerEntryV6ToV7(entry) {
    return {
        ...entry,
        version: 7,
    };
}
/**
 * Type guard to check if a ledger entry is V2
 *
 * @param entry - Ledger entry to check (can be V1 or V2)
 * @returns True if the entry is V2, false if V1
 */
function isCreditLedgerEntryV2(entry) {
    return "version" in entry && entry.version === 2;
}
/**
 * Upgrade a V1 ledger entry to V2 structure
 *
 * @internal This function is used internally by normalizeLedgerEntry. Most code should
 * use normalizeLedgerEntry instead.
 *
 * V1 entries don't have application-specific data, so we default to "general" application type.
 * This means the pricing context is approximated but not exact for V1 entries.
 *
 * @param entry - V1 ledger entry to upgrade
 * @param creditsUsedCalculator - Function to calculate credits from tokens and divisors
 * @returns Upgraded V2 ledger entry
 */
function upgradeLedgerEntryToV2(entry, creditsUsedCalculator) {
    const creditsUsed = creditsUsedCalculator(entry.tokensUsed, entry.tokenDivisors);
    return {
        version: 2,
        accountUid: entry.accountUid,
        period: entry.period,
        transactionId: entry.transactionId,
        tokenUsageByApplication: [
            {
                applicationType: exports.TokenUsageApplicationType.enum.general,
                tokensUsed: entry.tokensUsed,
                tokenDivisors: entry.tokenDivisors,
                creditsUsed,
            },
        ],
        appliedToBalance: entry.appliedToBalance,
        totalCreditsUsed: entry.totalCreditsUsed,
        totalCreditsApplied: entry.totalCreditsApplied,
        totalCreditsNotApplied: entry.totalCreditsNotApplied,
        stripeSyncStatus: exports.StripeSyncStatus.enum.none,
        stripeSyncedAt: undefined,
        stripeSyncError: undefined,
        stripeSyncRetries: 0,
        createdAt: entry.createdAt,
    };
}
/**
 * Normalize a ledger entry to always work with V6 structure
 *
 * This function provides lazy migration from V1/V2/V3/V4/V5 to V6. It should be called whenever reading
 * a ledger entry to ensure consistent handling across the codebase.
 *
 * **Usage:**
 * ```typescript
 * const rawEntry = await db.creditLedgerEntry(...).get();
 * const entry = normalizeLedgerEntry(rawEntry.data(), creditsUsedFromTokensUsed);
 * // Now work with entry as CreditLedgerEntryV7
 * ```
 *
 * @param entry - Ledger entry from storage (V1, V2, V3, V4, V5, V6, or V7)
 * @param creditsUsedCalculator - Function to calculate credits (needed for V1 upgrade)
 * @returns Normalized V7 ledger entry
 */
function normalizeLedgerEntry(entry, creditsUsedCalculator) {
    if (isCreditLedgerEntryV7(entry)) {
        return entry;
    }
    if (isCreditLedgerEntryV6(entry)) {
        return upgradeLedgerEntryV6ToV7(entry);
    }
    if (isCreditLedgerEntryV5(entry)) {
        return upgradeLedgerEntryV6ToV7(upgradeLedgerEntryV5ToV6(entry));
    }
    if (isCreditLedgerEntryV4(entry)) {
        return upgradeLedgerEntryV6ToV7(upgradeLedgerEntryV5ToV6(upgradeLedgerEntryV4ToV5(entry)));
    }
    if (isCreditLedgerEntryV3(entry)) {
        return upgradeLedgerEntryV6ToV7(upgradeLedgerEntryV5ToV6(upgradeLedgerEntryV4ToV5(upgradeLedgerEntryV3ToV4(entry))));
    }
    if (isCreditLedgerEntryV2(entry)) {
        return upgradeLedgerEntryV6ToV7(upgradeLedgerEntryV5ToV6(upgradeLedgerEntryV4ToV5(upgradeLedgerEntryV3ToV4(upgradeLedgerEntryV2ToV3(entry)))));
    }
    return upgradeLedgerEntryV6ToV7(upgradeLedgerEntryV5ToV6(upgradeLedgerEntryV4ToV5(upgradeLedgerEntryV3ToV4(upgradeLedgerEntryV2ToV3(upgradeLedgerEntryToV2(entry, creditsUsedCalculator))))));
}
exports.CreditUsageMap = zod_1.z.record(exports.CreditTokenType, exports.CreditAmount);
/**
 * Details that are recorded against the copilot trace - see also CopilotTrace["credit"] in copilot-utils (but that package
 * does not have access to the specific types like we do here, so it's a parallel implementation)
 */
exports.CreditTrace = zod_1.z.object({
    /**
     * Account that was debited
     */
    accountUid: zod_1.z.string().refine((val) => !!val),
    /**
     * The period this transaction was compared against or would be recorded against (always the current period)
     *
     * On success, allows looking up both the ledger entry (with this, accountUid and transactionId - if one was successfully
     * created) and the summary (with this and accountUid)
     */
    period: exports.CreditPeriod,
    /**
     * The balance type this transaction was recorded against (i.e. free or paid)
     *
     * Any given transaction is only recorded against a single credit type
     *
     * Note that "paid" here means "overage", on top of the credits that were preallocated for this period (irrespective
     * of whether those "free" credits were paid for in bulk, and irrespective of whether the overage will be charged for
     * directly by Stripe). Only some "paid" balances are pay-as-you-go.
     */
    appliedToBalance: exports.CreditBalanceType,
    /**
     * The number of credits consumed by each token type, for this recorded transaction
     *
     * This can be a non-integer amount of credits (for each token type)
     *
     * This record is partial, because new credit token types may have been added after this trace was generated
     */
    creditUsage: exports.CreditUsageMap,
    /**
     * The total number of credits used by this transaction - this is always an integer
     */
    totalCreditUsage: exports.TotalCreditAmount,
    /**
     * The total number of credits actually applied to the balance (up to the available balance)
     */
    totalCreditsApplied: exports.TotalCreditAmount,
    /**
     * The total number of credits that could not be applied due to insufficient balance
     */
    totalCreditsNotApplied: exports.TotalCreditAmount,
    /**
     * Whether the credit account had low free credit at the time the response was made
     */
    lowBalance: zod_1.z.boolean(),
}); // .and(CopilotTrace['credit']); (but core does not depend on copilot-utils - maybe it should)
exports.CreditPeriodType = zod_1.z.enum(["time-based", "static"]).optional().default("time-based");
/**
 * Response type returned by the backend summarizing the current period, so we can display the settings pane
 */
exports.CreditReport = zod_1.z.object({
    periodType: exports.CreditPeriodType,
    paidCreditPriceCents: structure_1.AmountInCents,
    undiscountedPaidCreditPriceCents: structure_1.AmountInCents.optional(),
    discountedPaidCreditPriceCents: structure_1.AmountInCents.optional(),
    discountApplied: zod_1.z.unknown().optional(),
    effectiveSpendingLimit: exports.SpendingLimit,
    spendingLimit: exports.SpendingLimit,
    freeCredits: exports.CreditAmount,
    freeCreditsUsed: exports.CreditAmount,
    freeCreditsRemaining: exports.CreditAmount.default(0), // TODO: remove the .default() after first deployment
    paidCreditsAtSpendingLimit: exports.CreditAmount,
    paidCreditsUsed: exports.CreditAmount,
    /**
     * The number of paid credits remaining, accounting for credits burned at different price points.
     * This is calculated as: (effectiveSpendingLimit - totalDollarsBurned) / currentPaidCreditPriceCents
     * where totalDollarsBurned sums up (credits * pricePerCredit) for each price tier.
     */
    paidCreditsRemaining: exports.CreditAmount.optional().default(0),
    canUsePaidCredits: zod_1.z.boolean(),
    hasRemainingFreeCredits: zod_1.z.boolean(),
    hasRemainingPaidCredits: zod_1.z.boolean(),
    paidCreditConsentGiven: zod_1.z.boolean().default(false),
});
/**
 * Reporting categorization for credit usage, that we use when e.g. crafting event attributes to send to analytics
 *
 * We'd like to track whether the user would have received the credits they used in this query from their
 * regular free credit allocation, had they still been on a free account type. i.e. has their upgraded free credit
 * allocation thanks to their paid plan been put to use in this query, or would they have been able to do this query
 * with the free allocation they were entitled to before they signed up
 *
 * This categorization also distinguishes between the pay-as-you-go accounting model (with time-based credit periods)
 * and preallocated/pre-sold credit usage blocks (with static credit periods)
 */
exports.CreditReportingCategory = zod_1.z.enum(["free", "free-from-paid-plan", "paid", "prepaid", "overage"]);
/**
 * Attribution for credit usage ('surface')
 *
 * We'd like to track what user facing feature is responsible for any particular credit burn. This is often seen as
 * the "surface" of the credit burn in analytics events.
 */
exports.CreditUsageAttribution = zod_1.z.enum(["Chat", "AutoLayout", "DatasheetImport", "Reviews"]);
/**
 * Tags for credit attribution
 *
 * Allows tracking multi-faceted details of credit burn. The distinction from the earlier `CreditUsageAttribution`, as
 * seen above, is that tracking/burn/ingest functions usually support receiving an array of these tags, not just one.
 */
exports.CreditUsageAttributionTag = zod_1.z.enum([
    "Project",
    "Copilot1500",
    "GenericLLM",
    "Autopilot",
    "Sandbox",
    "Orbit",
]);
//# sourceMappingURL=structure.js.map